/**
 * Jain Lexicon permission groups (names must match API; "contributer" spelling is backend).
 */
export const JAIN_LEXICON_GROUP = {
  admin: "jain_lexicon_admin",
  scholar: "jain_lexicon_scholar",
  contributor: "jain_lexicon_contributer",
} as const;

/** App-level Lexicon role used for UI (maps from group name or `app_role`). */
export type AppLexiconRole = "admin" | "scholar" | "contributor";

export type JainLexiconGroupRef = {
  id: number;
  name: string;
};

/** Stored RBAC snapshot for portal or staff session. */
export type JainLexiconRbacState = {
  appRole: AppLexiconRole | null;
  permissionGroup: JainLexiconGroupRef | null;
  permissions: number[];
  /** Backend `tenant_admin` | `tenant_scholar` | `tenant_contributor` | `tenant_user` */
  tenantUserType?: string | null;
  /** Mirrors `user.groups` from JWT/profile when present */
  authGroups?: JainLexiconGroupRef[];
};

export const JL_PORTAL_RBAC_KEY = "jl_portal_rbac";
export const JL_STAFF_RBAC_KEY = "jl_staff_rbac";

function parsePermissionIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(x => (typeof x === "number" ? x : Number(x)))
    .filter(n => Number.isFinite(n));
}

function groupFromUnknown(g: unknown): JainLexiconGroupRef | null {
  if (!g || typeof g !== "object" || Array.isArray(g)) {
    return null;
  }
  const o = g as Record<string, unknown>;
  const id = o.id;
  const name = o.name;
  if (
    (typeof id === "number" || typeof id === "string") &&
    typeof name === "string"
  ) {
    const numId = typeof id === "number" ? id : Number(id);
    if (!Number.isFinite(numId)) {
      return null;
    }
    return { id: numId, name };
  }
  return null;
}

function inferAppRoleFromGroupName(name: string): AppLexiconRole | null {
  const n = name.toLowerCase().trim();
  if (n === JAIN_LEXICON_GROUP.admin) {
    return "admin";
  }
  if (n === JAIN_LEXICON_GROUP.scholar) {
    return "scholar";
  }
  if (n === JAIN_LEXICON_GROUP.contributor) {
    return "contributor";
  }
  if (n.includes("admin")) {
    return "admin";
  }
  if (n.includes("scholar")) {
    return "scholar";
  }
  if (n.includes("contributer") || n.includes("contributor")) {
    return "contributor";
  }
  return null;
}

function groupsArrayFromUnknown(raw: unknown): JainLexiconGroupRef[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: JainLexiconGroupRef[] = [];
  for (const g of raw) {
    const ref = groupFromUnknown(g);
    if (ref) {
      out.push(ref);
    }
  }
  return out;
}

function inferAppRoleFromGroups(groups: JainLexiconGroupRef[]): AppLexiconRole | null {
  for (const g of groups) {
    const m = inferAppRoleFromGroupName(g.name);
    if (m) {
      return m;
    }
  }
  return null;
}

/** Map backend `user_type` to panel role for menus / guards. */
function appRoleFromTenantUserType(raw: unknown): AppLexiconRole | null {
  if (typeof raw !== "string") {
    return null;
  }
  const t = raw.toLowerCase().trim();
  if (t === "tenant_admin") {
    return "admin";
  }
  if (t === "tenant_scholar") {
    return "scholar";
  }
  if (t === "tenant_contributor") {
    return "contributor";
  }
  return null;
}

function normalizeAppRole(raw: unknown): AppLexiconRole | null {
  if (typeof raw !== "string") {
    return null;
  }
  const t = raw.toLowerCase().trim();
  if (t === "admin") {
    return "admin";
  }
  if (t === "scholar") {
    return "scholar";
  }
  if (t === "contributor") {
    return "contributor";
  }
  return null;
}

/**
 * Parse RBAC from login /auth/me/ / signup response blobs.
 */
export function parseRbacFromAuthPayload(
  blob: Record<string, unknown> | null,
  rawFallback?: unknown
): JainLexiconRbacState | null {
  const candidates: Record<string, unknown>[] = [];
  if (blob) {
    candidates.push(blob);
    const d = blob.data;
    if (d && typeof d === "object" && !Array.isArray(d)) {
      candidates.push(d as Record<string, unknown>);
    }
  }
  if (
    rawFallback &&
    typeof rawFallback === "object" &&
    !Array.isArray(rawFallback)
  ) {
    candidates.push(rawFallback as Record<string, unknown>);
  }

  for (const root of candidates) {
    const wrapped =
      root.user ??
      root.profile ??
      (root.data as Record<string, unknown> | undefined)?.user;
    let u: Record<string, unknown> | null = null;
    if (wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)    ) {
      u = wrapped as Record<string, unknown>;
    } else if (
      typeof root.email === "string" ||
      Array.isArray(root.permissions) ||
      Array.isArray(root.groups) ||
      root.group ||
      root.permission_group ||
      typeof root.app_role === "string" ||
      typeof root.user_type === "string"
    ) {
      u = root;
    }
    if (!u) {
      continue;
    }
    const permissions = parsePermissionIds(u.permissions);
    const authGroups = groupsArrayFromUnknown(u.groups);
    const permissionGroup =
      groupFromUnknown(u.group ?? u.permission_group) ??
      authGroups[0] ??
      null;

    let appRole =
      appRoleFromTenantUserType(u.role) ??
      appRoleFromTenantUserType(u.user_type) ??
      normalizeAppRole(u.role) ??
      normalizeAppRole(u.app_role) ??
      normalizeAppRole(u.lexicon_role) ??
      normalizeAppRole(u.portal_role);

    if (!appRole && permissionGroup?.name) {
      appRole = inferAppRoleFromGroupName(permissionGroup.name);
    }
    if (!appRole && authGroups.length) {
      appRole = inferAppRoleFromGroups(authGroups);
    }

    const tenantUserTypeRaw = u.user_type;
    const tenantUserType =
      typeof tenantUserTypeRaw === "string" ? tenantUserTypeRaw.trim() || null : null;

    const hasSignal =
      permissions.length ||
      permissionGroup ||
      appRole ||
      authGroups.length > 0 ||
      !!tenantUserType;

    if (hasSignal) {
      return {
        appRole,
        permissionGroup,
        permissions,
        tenantUserType,
        ...(authGroups.length ? { authGroups } : {}),
      };
    }
  }
  return null;
}

export function getStoredPortalRbac(): JainLexiconRbacState | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(JL_PORTAL_RBAC_KEY);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw) as JainLexiconRbacState;
    if (!o || typeof o !== "object") {
      return null;
    }
    return {
      appRole: o.appRole ?? null,
      permissionGroup: o.permissionGroup ?? null,
      permissions: Array.isArray(o.permissions) ? o.permissions : [],
      tenantUserType:
        typeof o.tenantUserType === "string" ? o.tenantUserType : null,
      authGroups: Array.isArray(o.authGroups) ? o.authGroups : undefined,
    };
  } catch {
    return null;
  }
}

export function setStoredPortalRbac(state: JainLexiconRbacState | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (!state) {
    localStorage.removeItem(JL_PORTAL_RBAC_KEY);
    return;
  }
  localStorage.setItem(JL_PORTAL_RBAC_KEY, JSON.stringify(state));
}

export function getStoredStaffRbac(): JainLexiconRbacState | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(JL_STAFF_RBAC_KEY);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw) as JainLexiconRbacState;
    if (!o || typeof o !== "object") {
      return null;
    }
    return {
      appRole: o.appRole ?? null,
      permissionGroup: o.permissionGroup ?? null,
      permissions: Array.isArray(o.permissions) ? o.permissions : [],
      tenantUserType:
        typeof o.tenantUserType === "string" ? o.tenantUserType : null,
      authGroups: Array.isArray(o.authGroups) ? o.authGroups : undefined,
    };
  } catch {
    return null;
  }
}

export function setStoredStaffRbac(state: JainLexiconRbacState | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (!state) {
    localStorage.removeItem(JL_STAFF_RBAC_KEY);
    return;
  }
  localStorage.setItem(JL_STAFF_RBAC_KEY, JSON.stringify(state));
}

export function clearAllStoredRbac(): void {
  setStoredPortalRbac(null);
  setStoredStaffRbac(null);
}

export function rbacHasPermission(
  state: JainLexiconRbacState | null,
  permissionId: number
): boolean {
  if (!state?.permissions?.length) {
    return false;
  }
  return state.permissions.includes(permissionId);
}

export function rbacHasRole(
  state: JainLexiconRbacState | null,
  role: AppLexiconRole
): boolean {
  return state?.appRole === role;
}

export function rbacIsLexiconAdmin(state: JainLexiconRbacState | null): boolean {
  return state?.appRole === "admin";
}

export function rbacIsScholar(state: JainLexiconRbacState | null): boolean {
  return state?.appRole === "scholar";
}

export function rbacIsContributor(state: JainLexiconRbacState | null): boolean {
  return state?.appRole === "contributor";
}
